/**
 * Pick Your Favorites
 * Product Card Controller
 *
 * Responsibilities:
 * - read candy and inventory information from product cards
 * - add and remove picks
 * - prevent selections beyond Shopify inventory
 * - include candy quantities already reserved in the cart
 * - keep product quantities synchronized with state
 * - disable increase buttons when limits are reached
 */

window.PYF = window.PYF || {};


/* =====================================================
   GET PRODUCT CARDS
===================================================== */

PYF.getProductCards = function() {
  return Array.from(
    document.querySelectorAll(
      ".pyf-candy-card"
    )
  );
};


/* =====================================================
   DATA HELPERS
===================================================== */

PYF.parseBoolean = function(value) {
  return (
    String(value).toLowerCase() ===
    "true"
  );
};


PYF.parseInventoryQuantity =
  function(value) {
    const quantity =
      Number.parseInt(
        value,
        10
      );

    if (
      !Number.isFinite(quantity)
    ) {
      return 0;
    }

    return Math.max(
      0,
      quantity
    );
  };


/* =====================================================
   READ PRODUCT DATA
===================================================== */

PYF.getProductFromCard =
  function(card) {
    if (!card) {
      return null;
    }

    const productId =
      card.dataset.productId ||
      card.dataset.variantId ||
      "";

    const variantId =
      card.dataset.variantId ||
      card.dataset.productId ||
      "";

    const title =
      card.dataset.productTitle ||
      card.dataset.title ||
      card
        .querySelector("h3")
        ?.textContent
        ?.trim() ||
      "Candy";

    const image =
      card.dataset.image ||
      card
        .querySelector(
          ".pyf-candy-image img"
        )
        ?.currentSrc ||
      card
        .querySelector(
          ".pyf-candy-image img"
        )
        ?.src ||
      "";

    const inventoryTracked =
      PYF.parseBoolean(
        card.dataset
          .inventoryTracked
      );

    const inventoryLimited =
      PYF.parseBoolean(
        card.dataset
          .inventoryLimited
      );

    const inventoryQuantity =
      PYF.parseInventoryQuantity(
        card.dataset
          .inventoryQuantity
      );

    const variantAvailable =
      PYF.parseBoolean(
        card.dataset
          .variantAvailable
      );

    const inventoryPolicy =
      card.dataset
        .inventoryPolicy ||
      "";

    if (
      !productId &&
      !variantId
    ) {
      console.error(
        "PYF.getProductFromCard: Missing product or variant ID.",
        card
      );

      return null;
    }

    return {
      productId:
        String(
          productId ||
          variantId
        ),

      variantId:
        String(
          variantId ||
          productId
        ),

      title: title,

      image: image,

      inventoryTracked:
        inventoryTracked,

      inventoryLimited:
        inventoryLimited,

      inventoryQuantity:
        inventoryQuantity,

      inventoryPolicy:
        inventoryPolicy,

      variantAvailable:
        variantAvailable
    };
  };


/* =====================================================
   GET CARD CONTROLS
===================================================== */

PYF.getProductControls =
  function(card) {
    return {
      selector:
        card.querySelector(
          ".pyf-quantity-selector"
        ),

      addButton:
        card.querySelector(
          ".pyf-add-button"
        ),

      decreaseButton:
        card.querySelector(
          ".pyf-decrease"
        ),

      increaseButton:
        card.querySelector(
          ".pyf-increase"
        ),

      quantity:
        card.querySelector(
          ".pyf-quantity"
        ),

      inventoryMessage:
        card.querySelector(
          ".pyf-inventory-message"
        )
    };
  };


/* =====================================================
   INVENTORY HELPERS
===================================================== */

PYF.isProductInventoryLimited =
  function(product) {
    return Boolean(
      product &&
      product.inventoryTracked &&
      product.inventoryLimited
    );
  };


/**
 * Quantity already inside completed PYF bags
 * currently sitting in the Shopify cart.
 */
PYF.getProductReservedQuantity =
  function(product) {
    if (
      !product ||
      typeof PYF
        .getReservedInventory !==
        "function"
    ) {
      return 0;
    }

    return Math.max(
      0,
      Number(
        PYF.getReservedInventory(
          product.variantId
        )
      ) || 0
    );
  };


/**
 * Inventory available for the new bag after
 * subtracting completed bags already in cart.
 *
 * Example:
 * Shopify inventory: 10
 * Already reserved in cart: 6
 * Available for new bag: 4
 */
PYF.getProductAvailableInventory =
  function(product) {
    if (!product) {
      return 0;
    }

    if (
      !PYF
        .isProductInventoryLimited(
          product
        )
    ) {
      return Infinity;
    }

    const reservedQuantity =
      PYF
        .getProductReservedQuantity(
          product
        );

    return Math.max(
      0,
      product.inventoryQuantity -
        reservedQuantity
    );
  };


/**
 * Inventory still remaining after subtracting:
 *
 * 1. completed bags in cart
 * 2. current unfinished bag
 */
PYF.getProductRemainingInventory =
  function(product) {
    if (!product) {
      return 0;
    }

    if (
      !PYF
        .isProductInventoryLimited(
          product
        )
    ) {
      return Infinity;
    }

    const availableInventory =
      PYF
        .getProductAvailableInventory(
          product
        );

    const selectedQuantity =
      typeof PYF
        .getItemQuantity ===
        "function"
        ? PYF.getItemQuantity(
            product.productId
          )
        : 0;

    return Math.max(
      0,
      availableInventory -
        selectedQuantity
    );
  };


PYF.hasReachedInventoryLimit =
  function(product) {
    if (
      !PYF
        .isProductInventoryLimited(
          product
        )
    ) {
      return false;
    }

    return (
      PYF
        .getProductRemainingInventory(
          product
        ) <= 0
    );
  };


PYF.isProductSoldOut =
  function(product) {
    if (!product) {
      return true;
    }

    if (
      !product.variantAvailable
    ) {
      return true;
    }

    if (
      PYF
        .isProductInventoryLimited(
          product
        ) &&
      PYF
        .getProductAvailableInventory(
          product
        ) <= 0
    ) {
      return true;
    }

    return false;
  };


/* =====================================================
   INVENTORY MESSAGE
===================================================== */

PYF.updateInventoryMessage =
  function(
    product,
    controls,
    quantity
  ) {
    if (
      !controls.inventoryMessage
    ) {
      return;
    }

    const message =
      controls.inventoryMessage;

    const reservedQuantity =
      PYF
        .getProductReservedQuantity(
          product
        );

    const availableInventory =
      PYF
        .getProductAvailableInventory(
          product
        );


    if (
        PYF.isProductSoldOut(
        product
     )
     ) {
        message.textContent = "";
        message.hidden = true;

        return;
    }

    if (
      PYF
        .isProductInventoryLimited(
          product
        ) &&
      quantity >=
        availableInventory
    ) {
      const unitLabel =
        availableInventory === 1
          ? "pick"
          : "picks";

      message.textContent =
        `Only ${availableInventory} ${unitLabel} available for this bag`;

      message.hidden = false;

      return;
    }

    message.textContent = "";
    message.hidden = true;
  };


/* =====================================================
   RENDER ONE PRODUCT CARD
===================================================== */

PYF.renderProductCard =
  function(card) {
    const product =
      PYF.getProductFromCard(
        card
      );

    if (!product) {
      return;
    }

    const controls =
      PYF.getProductControls(
        card
      );

    const quantity =
      typeof PYF
        .getItemQuantity ===
        "function"
        ? PYF.getItemQuantity(
            product.productId
          )
        : 0;

    const hasQuantity =
      quantity > 0;

    const bagIsFull =
      typeof PYF.isBagFull ===
        "function" &&
      PYF.isBagFull();

    const soldOut =
      PYF.isProductSoldOut(
        product
      );

    const inventoryLimitReached =
      PYF
        .hasReachedInventoryLimit(
          product
        );

    const reservedQuantity =
      PYF
        .getProductReservedQuantity(
          product
        );

    const remainingInventory =
      PYF
        .getProductRemainingInventory(
          product
        );

    if (controls.quantity) {
      controls.quantity.textContent =
        String(quantity);
    }

    if (controls.selector) {
      controls.selector
        .classList.toggle(
          "active",
          hasQuantity
        );
    }

    if (controls.addButton) {
      controls.addButton.hidden =
        hasQuantity;

      controls.addButton.disabled =
        soldOut ||
        bagIsFull ||
        inventoryLimitReached;

     controls.addButton.textContent =
        soldOut
        ? "Out of Stock"
        : "Add to Bag";
     }

    if (
      controls.decreaseButton
    ) {
      controls.decreaseButton
        .disabled =
        !hasQuantity;
    }

    if (
      controls.increaseButton
    ) {
      controls.increaseButton
        .disabled =
        soldOut ||
        bagIsFull ||
        inventoryLimitReached;
    }

    PYF.updateInventoryMessage(
      product,
      controls,
      quantity
    );

    card.classList.toggle(
      "has-selection",
      hasQuantity
    );

    card.classList.toggle(
      "is-sold-out",
      soldOut
    );

    card.classList.toggle(
      "inventory-limit-reached",
      inventoryLimitReached
    );

    card.setAttribute(
      "data-current-quantity",
      String(quantity)
    );

    card.setAttribute(
      "data-cart-reserved-quantity",
      String(reservedQuantity)
    );

    card.setAttribute(
      "data-remaining-inventory",
      Number.isFinite(
        remainingInventory
      )
        ? String(
            remainingInventory
          )
        : "unlimited"
    );
  };


/* =====================================================
   RENDER ALL PRODUCT CARDS
===================================================== */

PYF.renderProducts =
  function() {
    PYF.getProductCards()
      .forEach(
        function(card) {
          PYF.renderProductCard(
            card
          );
        }
      );
  };


/* =====================================================
   SHOW BAG-FULL MESSAGE
===================================================== */

PYF.showBagFullMessage =
  function() {
    if (
      typeof PYF.showToast ===
      "function"
    ) {
      PYF.showToast(
        "Bag is full",
        "Remove a pick or add this bag to the cart before selecting more candy."
      );

      return;
    }

    console.warn(
      "Bag is full. Remove a pick before adding another candy."
    );
  };


/* =====================================================
   SHOW INVENTORY-LIMIT MESSAGE
===================================================== */

PYF.showInventoryLimitMessage =
  function(product) {
    if (!product) {
      return;
    }

    const availableInventory =
      PYF
        .getProductAvailableInventory(
          product
        );

    const reservedQuantity =
      PYF
        .getProductReservedQuantity(
          product
        );

    let message = "";

    if (
      availableInventory <= 0 &&
      reservedQuantity > 0
    ) {
      message =
        `${product.title} is already fully reserved in your cart.`;
    } else {
      const unitLabel =
        availableInventory === 1
          ? "pick"
          : "picks";

      message =
        `${product.title} only has ${availableInventory} ${unitLabel} available for this bag.`;
    }

    if (
      typeof PYF.showToast ===
      "function"
    ) {
      PYF.showToast(
        "Inventory limit reached",
        message
      );

      return;
    }

    console.warn(message);
  };


/* =====================================================
   SHOW SOLD-OUT MESSAGE
===================================================== */

PYF.showSoldOutMessage =
  function(product) {
    if (!product) {
      return;
    }

    const reservedQuantity =
      PYF
        .getProductReservedQuantity(
          product
        );

    let message =
      `${product.title} is currently unavailable.`;

    if (
      reservedQuantity > 0 &&
      product.inventoryQuantity > 0
    ) {
      message =
        `${product.title} is already fully reserved in your cart.`;
    }

    if (
      typeof PYF.showToast ===
      "function"
    ) {
      PYF.showToast(
        "Unavailable",
        message
      );

      return;
    }

    console.warn(message);
  };


/* =====================================================
   ADD PICK
===================================================== */

PYF.addProductFromCard =
  function(card) {
    const product =
      PYF.getProductFromCard(
        card
      );

    if (!product) {
      return;
    }

    if (
      PYF.isProductSoldOut(
        product
      )
    ) {
      PYF.showSoldOutMessage(
        product
      );

      PYF.renderProductCard(
        card
      );

      return;
    }

    if (
      PYF
        .hasReachedInventoryLimit(
          product
        )
    ) {
      PYF
        .showInventoryLimitMessage(
          product
        );

      PYF.renderProductCard(
        card
      );

      return;
    }

    if (
      typeof PYF.isBagFull ===
        "function" &&
      PYF.isBagFull()
    ) {
      PYF.showBagFullMessage();

      return;
    }

    const result =
      PYF.addItem(product);

    if (
      !result ||
      result.success !== false
    ) {
      return;
    }

    if (
      result.reason ===
      "bag-full"
    ) {
      PYF.showBagFullMessage();
    }
  };


/* =====================================================
   REMOVE PICK
===================================================== */

PYF.removeProductFromCard =
  function(card) {
    const product =
      PYF.getProductFromCard(
        card
      );

    if (!product) {
      return;
    }

    PYF.removeItem(
      product.productId
    );
  };

/* =====================================================
   FIND PRODUCT CARD BY VARIANT ID
===================================================== */

PYF.getProductCardByVariantId =
  function(variantId) {

    const targetId =
      String(variantId || "");

    if (!targetId) {
      return null;
    }

    return (
      PYF.getProductCards().find(
        function(card) {

          const cardVariantId =
            String(
              card.dataset.variantId ||
              ""
            );

          return (
            cardVariantId === targetId
          );
        }
      ) || null
    );
  };


/* =====================================================
   RESTORE BAG FOR EDITING
===================================================== */

PYF.restoreBagForEditing =
  function(editData) {

    if (
      !editData ||
      !PYF.state
    ) {
      console.error(
        "PYF.restoreBagForEditing: Invalid edit data."
      );

      return {
        success: false,
        restoredPicks: 0
      };
    }


    const bagSize =
      String(
        editData.bagSize ||
        "½ lb"
      );


    const maxPicks =
      Math.max(
        1,
        Number.parseInt(
          editData.maxPicks,
          10
        ) || 8
      );


    const bagVariantId =
      String(
        editData.bagVariantId ||
        ""
      );


    const reservations =
      Array.isArray(
        editData.candyReservations
      )
        ? editData.candyReservations
        : [];


    /*
     * Restore selected bag.
     */

    PYF.state.bag = {

      id:
        `pyf-bag-${maxPicks}`,

      size:
        bagSize,

      maxPicks:
        maxPicks,

      variantId:
        bagVariantId || null

    };


    /*
     * Start with a clean selection.
     */

    PYF.state.items = {};

    PYF.state.currentPicks = 0;


    /*
     * Restore each candy by matching the
     * saved Shopify variant ID to the
     * candy product card.
     */

    reservations.forEach(
      function(savedCandy) {

        if (
          PYF.state.currentPicks >=
          maxPicks
        ) {
          return;
        }


        const variantId =
          String(
            savedCandy.variantId ||
            ""
          );


        const requestedQuantity =
          Math.max(
            0,
            Number.parseInt(
              savedCandy.quantity,
              10
            ) || 0
          );


        if (
          !variantId ||
          requestedQuantity <= 0
        ) {
          return;
        }


        const card =
          PYF.getProductCardByVariantId(
            variantId
          );


        if (!card) {

          console.warn(
            "PYF edit bag: Candy card not found for variant:",
            variantId
          );

          return;
        }


        const product =
          PYF.getProductFromCard(
            card
          );


        if (!product) {
          return;
        }


        /*
         * Don't restore more than the
         * remaining capacity of the bag.
         */

        const bagSpace =
          Math.max(
            0,
            maxPicks -
              PYF.state.currentPicks
          );


        let quantity =
          Math.min(
            requestedQuantity,
            bagSpace
          );


        /*
         * Respect current inventory.
         *
         * By the time this runs, the old
         * cart line will already have been
         * removed and cart reservations
         * refreshed.
         */

        const availableInventory =
          PYF.getProductAvailableInventory(
            product
          );


        if (
          Number.isFinite(
            availableInventory
          )
        ) {

          quantity =
            Math.min(
              quantity,
              availableInventory
            );
        }


        if (quantity <= 0) {
          return;
        }


        PYF.state.items[
          product.productId
        ] = {

          productId:
            product.productId,

          variantId:
            product.variantId,

          title:
            product.title,

          image:
            product.image,

          inventoryTracked:
            product.inventoryTracked,

          inventoryLimited:
            product.inventoryLimited,

          inventoryQuantity:
            product.inventoryQuantity,

          inventoryPolicy:
            product.inventoryPolicy,

          variantAvailable:
            product.variantAvailable,

          quantity:
            quantity

        };


        PYF.state.currentPicks +=
          quantity;
      }
    );


    /*
     * Tell sidebar, product cards and
     * bag selector to redraw.
     */

    if (
      typeof PYF.emitStateChange ===
      "function"
    ) {

      PYF.emitStateChange(
        "bag-restored-for-edit"
      );
    }


    console.log(
      "🍬 PYF bag restored for editing:",
      {
        bagSize:
          bagSize,

        maxPicks:
          maxPicks,

        restoredPicks:
          PYF.state.currentPicks
      }
    );


    return {
      success: true,
      restoredPicks:
        PYF.state.currentPicks
    };
  };

/* =====================================================
   CANDY FILTERS
===================================================== */

PYF.filterCandies = function(filterValue) {

  const cards =
    PYF.getProductCards();

  let visibleCount = 0;

  const normalizedFilter =
    String(filterValue || "")
      .trim()
      .toLowerCase();


  cards.forEach(function(card) {

    if (
      normalizedFilter === "all"
    ) {
      card.style.display = "";
      visibleCount += 1;
      return;
    }


    const tags =
      String(
        card.dataset.pyfTags || ""
      )
        .split("|")
        .map(function(tag) {
          return tag
            .trim()
            .toLowerCase();
        });


    const matches =
      tags.includes(
        normalizedFilter
      );


    card.style.display =
      matches
        ? ""
        : "none";


    if (matches) {
      visibleCount += 1;
    }

  });


  const emptyMessage =
    document.querySelector(
      "[data-pyf-filter-empty]"
    );


  if (emptyMessage) {
    emptyMessage.hidden =
      visibleCount > 0;
  }

};

/* =====================================================
   FILTER BUTTON LISTENER
===================================================== */

document.addEventListener(
  "click",
  function(event) {

    const button =
      event.target.closest(
        "[data-pyf-filter]"
      );


    if (!button) {
      return;
    }


    event.preventDefault();


    const filterValue =
      button.dataset.pyfFilter ||
      "all";


    const buttons =
      document.querySelectorAll(
        "[data-pyf-filter]"
      );


    buttons.forEach(
      function(otherButton) {

        const isActive =
          otherButton === button;


        otherButton.classList.toggle(
          "active",
          isActive
        );


        otherButton.setAttribute(
          "aria-pressed",
          isActive
            ? "true"
            : "false"
        );

      }
    );


    PYF.filterCandies(
      filterValue
    );

  }
);

/* =====================================================
   INITIALIZE PRODUCT CARDS
===================================================== */

PYF.initProducts =
  function() {
    const cards =
      PYF.getProductCards();

    if (!cards.length) {
      console.warn(
        "PYF.initProducts: No candy cards found."
      );

      return;
    }

    cards.forEach(
      function(card) {
        if (
          card.dataset
            .pyfProductInitialized ===
          "true"
        ) {
          return;
        }

        card.dataset
          .pyfProductInitialized =
          "true";

        const controls =
          PYF.getProductControls(
            card
          );

        if (
          controls.addButton
        ) {
          controls.addButton.type =
            "button";

          controls.addButton
            .addEventListener(
              "click",
              function(event) {
                event.preventDefault();
                event.stopPropagation();

                PYF
                  .addProductFromCard(
                    card
                  );
              }
            );
        }

        if (
          controls.increaseButton
        ) {
          controls.increaseButton.type =
            "button";

          controls.increaseButton
            .addEventListener(
              "click",
              function(event) {
                event.preventDefault();
                event.stopPropagation();

                PYF
                  .addProductFromCard(
                    card
                  );
              }
            );
        }

        if (
          controls.decreaseButton
        ) {
          controls.decreaseButton.type =
            "button";

          controls.decreaseButton
            .addEventListener(
              "click",
              function(event) {
                event.preventDefault();
                event.stopPropagation();

                PYF
                  .removeProductFromCard(
                    card
                  );
              }
            );
        }
      }
    );

    PYF.renderProducts();
  };


/* =====================================================
   STATE CHANGE LISTENER
===================================================== */

document.addEventListener(
  "pyf:state-changed",
  function() {
    PYF.renderProducts();
  }
);


/* =====================================================
   CART RESERVATION CHANGE LISTENER
===================================================== */

document.addEventListener(
  "pyf:cart-reservations-changed",
  function() {
    PYF.renderProducts();
  }
);


/* =====================================================
   BAG ADDED LISTENER
===================================================== */

document.addEventListener(
  "pyf:bag-added",
  function() {
    PYF.renderProducts();
  }
);